import {
  Dropdown,
  dropdownTypes,
  Modal,
  NewButton,
  NewButtonVariant,
} from "@visiblethread/react-components";
import Field, { fieldTypes } from "components/Field";
import { incorrectNumberOfUsersModal } from "components/Modal/confModalTypes";
import { licenseTypeLabel } from "constants/licenseTypes";
import { modalTypes } from "constants/types";
import PropTypes from "prop-types";
import React, { useId, useState } from "react";
import { useDispatch } from "react-redux";
import { hideModal, showModal } from "slices/app/actions/modals";
import { removeErrorMessage } from "slices/app/errorSlice";
import { updateLicense } from "slices/systemAdmin/actions/license";
import {
  setWorkspaceLicense,
  setWorkspaceNotes,
} from "slices/systemAdmin/systemAdminSlice";
import { DATE_VARIANTS, expiryDateValidator, formatDate } from "utils";

import {
  getOptionButton,
  LICENSE_TYPE_OPTIONS,
} from "./licenseTypeDropdown";

const EditLicensingModal = ({
  workspaceId,
  license,
  notes: workspaceNotes,
  licensedUsers,
}) => {
  const dispatch = useDispatch();

  const [licenseType, setLicenseType] = useState(
    license?.licenseType || "Docs - Internal"
  );
  const [numberUsers, setNumberUsers] = useState(
    license?.numberOfUsers ? parseInt(license?.numberOfUsers).toString() : "0"
  );
  const [notes, setNotes] = useState(workspaceNotes || "");
  const [numberUsersError, setNumberUsersError] = useState("");
  const [expiryDate, setExpiryDate] = useState(
    license?.expiryDate
      ? new Date(
          license?.expiryDate[0],
          license?.expiryDate[1] - 1,
          license?.expiryDate[2],
          0,
          0
        )
      : new Date()
  );
  const [expiryDateError, setExpiryDateError] = useState("");
  const notesId = useId();

  const isDisabled = () => numberUsersError !== "" || expiryDateError !== "";

  const handleNotesChange = ({ target: { value } }) => {
    setNotes(value);
  };

  const handleClose = () => {
    dispatch(hideModal(modalTypes.EDIT_LICENSING));
  };

  const handleSuccess = () => {
    dispatch(
      setWorkspaceLicense({
        workspaceId,
        license: {
          ...license,
          licenseType,
          numberOfUsers: numberUsers,
          expiryDate: [
            expiryDate.getFullYear(),
            expiryDate.getMonth() + 1,
            expiryDate.getDate(),
          ],

          formattedExpiryDate: new Date(
            expiryDate.getFullYear(),
            expiryDate.getMonth(),
            expiryDate.getDate()
          ).toLocaleDateString("en-US", {
            day: "2-digit",
            month: "short",
            year: "numeric",
          }),
        },
      })
    );
    dispatch(setWorkspaceNotes({ workspaceId, notes }));
    handleClose();
  };

  const onSubmit = () => {
    if (numberUsers < licensedUsers) {
      dispatch(
        showModal(
          modalTypes.CONFIRMATION,
          incorrectNumberOfUsersModal(dispatch)
        )
      );
      handleClose();
      return;
    }

    dispatch(removeErrorMessage());
    dispatch(
      updateLicense(
        workspaceId,
        licenseType,
        numberUsers,
        formatDate(expiryDate, DATE_VARIANTS.ISO),
        notes,
        handleSuccess
      )
    );
  };

  return (
    <Modal
      data-testid="edit-licensing-modal"
      className="fade-in edit_licensing_modal"
      show
      onDismiss={handleClose}
      backdrop
      centered
    >
      <Modal.Header>
        <b>Edit Licensing</b>
      </Modal.Header>
      <Modal.Body>
        <div className="form__field__license_type">
          <b>License Type</b>
          <Dropdown
            type={dropdownTypes.buttons}
            propsObject={{
              text: licenseTypeLabel(licenseType),
              options: LICENSE_TYPE_OPTIONS.map((o) =>
                getOptionButton({ option: o, setLicenseType })
              ),
              buttonVariant: "l2",
            }}
          />
        </div>
        <Field
          type={fieldTypes.integer}
          propsObject={{
            value: numberUsers,
            setValue: setNumberUsers,
            setValueError: setNumberUsersError,
            valueError: numberUsersError,
            labelText: "Number Users",
          }}
        />

        <Field
          type={fieldTypes.date}
          propsObject={{
            date: expiryDate || new Date(),
            setDate: setExpiryDate,
            dateError: expiryDateError,
            setDateError: setExpiryDateError,
            labelText: "Expiry Date",
            validator: expiryDateValidator,
          }}
        />

        <label htmlFor={notesId} className="form__field__notes">
          <b>Notes</b>
          <textarea
            id={notesId}
            data-testid="notes-input"
            rows={4}
            value={notes}
            onChange={handleNotesChange}
          />
        </label>
      </Modal.Body>
      <Modal.Footer>
        <NewButton
          data-testid="cancel-button"
          variant={NewButtonVariant.l2}
          onClick={handleClose}
        >
          Cancel
        </NewButton>

        <NewButton
          variant={NewButtonVariant.l1}
          onClick={onSubmit}
          disabled={isDisabled()}
          data-testid="ok-button"
        >
          Ok
        </NewButton>
      </Modal.Footer>
    </Modal>
  );
};

export default EditLicensingModal;

EditLicensingModal.propTypes = {
  workspaceId: PropTypes.number.isRequired,

  license: PropTypes.objectOf(PropTypes.any).isRequired,
  notes: PropTypes.string.isRequired,
  licensedUsers: PropTypes.number.isRequired,
};
